// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// SetListPage: the author's blueprint sets (blueprint-sets Req 1.1, 1.5, 1.6).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { api } from '../../api/client';
import { useSets } from '../../api/hooks';
import { formatDate } from '../../lib/format';
import { ErrorDetail, errorMessage, useNotify } from '../../lib/notify';
import { RouterLink } from './setShared';

// ---------- SetListPage (Req 1.1, 1.5, 1.6) ----------

type SetRow = Awaited<ReturnType<typeof api.listSets>>['items'][number];

export function SetListPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const [archived, setArchived] = useState(false);
  const [archiving, setArchiving] = useState<SetRow | null>(null);
  const [busy, setBusy] = useState(false);
  const query = useSets({ archived: archived ? 'true' : 'false' });
  const items = query.data?.items ?? [];

  const duplicate = async (s: SetRow) => {
    try {
      const copy = await api.duplicateSet(s.id);
      notify.success(`Created "${copy.name}".`);
      await qc.invalidateQueries({ queryKey: ['sets'] });
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  };
  const archive = async () => {
    if (!archiving) return;
    setBusy(true);
    try {
      await api.archiveSet(archiving.id);
      notify.success(`Archived "${archiving.name}".`);
      setArchiving(null);
      await qc.invalidateQueries({ queryKey: ['sets'] });
    } catch (e) {
      notify.error(errorMessage(e), e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Table<SetRow>
        header={
          <Header
            variant="h1"
            counter={query.data ? `(${items.length}${query.data.nextCursor ? '+' : ''})` : undefined}
            description="Group your blueprints so a mixed packet is processed with the right blueprint for each document."
            actions={
              <Button variant="primary" onClick={() => navigate('/sets/new')}>
                Create set
              </Button>
            }
          >
            Blueprint sets
          </Header>
        }
        filter={
          <SegmentedControl
            label="Show sets"
            selectedId={archived ? 'archived' : 'active'}
            options={[
              { id: 'active', text: 'Active' },
              { id: 'archived', text: 'Archived' },
            ]}
            onChange={(e) => setArchived(e.detail.selectedId === 'archived')}
          />
        }
        loading={query.isLoading}
        loadingText="Loading blueprint sets"
        items={items}
        trackBy="id"
        empty={
          <Box textAlign="center" color="inherit">
            {query.isError ? (
              <ErrorDetail error={query.error} />
            ) : (
              <>
                <b>{archived ? 'No archived sets' : 'No blueprint sets yet'}</b>
                {!archived && (
                  <Box padding={{ top: 's' }}>
                    <Button onClick={() => navigate('/sets/new')}>Create set</Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (s) => <RouterLink to={`/sets/${s.id}`}>{s.name}</RouterLink>,
            isRowHeader: true,
          },
          { id: 'description', header: 'Description', cell: (s) => s.description || '-' },
          { id: 'members', header: 'Members', cell: (s) => s.memberCount },
          {
            id: 'version',
            header: 'Latest version',
            cell: (s) => (s.latestVersion ? `Version ${s.latestVersion}` : 'Not published'),
          },
          { id: 'updated', header: 'Last modified', cell: (s) => formatDate(s.updatedAt) },
          { id: 'runs', header: 'Runs', cell: (s) => s.runCount },
          {
            id: 'actions',
            header: 'Actions',
            cell: (s) => (
              <ButtonDropdown
                variant="inline-icon"
                ariaLabel={`Actions for ${s.name}`}
                items={[
                  { id: 'run', text: 'Run a document' },
                  { id: 'duplicate', text: 'Duplicate' },
                  ...(s.archived ? [] : [{ id: 'archive', text: 'Archive' }]),
                ]}
                onItemClick={(e) => {
                  if (e.detail.id === 'run') navigate(`/run?setId=${encodeURIComponent(s.id)}`);
                  if (e.detail.id === 'duplicate') void duplicate(s);
                  if (e.detail.id === 'archive') setArchiving(s);
                }}
              />
            ),
          },
        ]}
      />
      <Modal
        visible={archiving !== null}
        onDismiss={() => setArchiving(null)}
        header={archiving ? `Archive "${archiving.name}"?` : 'Archive set'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setArchiving(null)}>
                Cancel
              </Button>
              <Button variant="primary" loading={busy} onClick={() => void archive()}>
                Archive
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        The set is hidden from this list and stays available under "Archived" with its versions and runs. Its blueprints
        do not change.
      </Modal>
    </>
  );
}
